import type { Circuit, TrackCell } from '../types/track.js';
import { TrackCellType } from '../types/track.js';
import { type CircuitPathBounds, isPathClosed, rebuildGridFromPath } from './circuitEdit.js';

export const DEFAULT_CIRCUIT_GRID_SIZE = 100;

/** Nombre minimal de cases (ouvertes) avant de pouvoir fermer le circuit. */
export const MIN_OPEN_CELLS_BEFORE_CLOSE = 4;

export function createBlankCircuit(
  width = DEFAULT_CIRCUIT_GRID_SIZE,
  height = DEFAULT_CIRCUIT_GRID_SIZE
): Circuit {
  return {
    width,
    height,
    grid: Array(height)
      .fill(null)
      .map(() => Array(width).fill(TrackCellType.EMPTY)),
    path: [],
  };
}

export function areCellsAdjacent(ax: number, ay: number, bx: number, by: number): boolean {
  return Math.abs(ax - bx) + Math.abs(ay - by) === 1;
}

function isWithinGrid(circuit: Circuit, x: number, y: number): boolean {
  return x >= 0 && x < circuit.width && y >= 0 && y < circuit.height;
}

function isCellOnOpenPath(path: TrackCell[], x: number, y: number): boolean {
  const end = isPathClosed(path) ? path.length - 1 : path.length;
  for (let i = 0; i < end; i++) {
    if (path[i].x === x && path[i].y === y) return true;
  }
  return false;
}

function withPath(circuit: Circuit, path: TrackCell[]): Circuit {
  const next = { ...circuit, path };
  return { ...next, grid: rebuildGridFromPath(next) };
}

/** Ouvre un circuit fermé pour permettre l’extension ou l’effacement. */
export function openPathForGeometryEdit(circuit: Circuit): Circuit {
  if (!isPathClosed(circuit.path)) return circuit;
  return withPath(circuit, circuit.path.slice(0, -1));
}

export type DrawPathCellResult = { circuit: Circuit; closed: boolean } | { error: string };

/**
 * Ajoute une case au tracé ou ferme la boucle en cliquant sur le départ.
 */
export function tryDrawPathCell(circuit: Circuit, x: number, y: number): DrawPathCellResult {
  if (!isWithinGrid(circuit, x, y)) {
    return { error: 'Position hors de la grille' };
  }

  const path = circuit.path;
  if (isPathClosed(path)) {
    return { error: 'Circuit fermé — utilisez Effacer pour modifier le tracé' };
  }

  if (path.length === 0) {
    return {
      circuit: withPath(circuit, [{ x, y, type: TrackCellType.START }]),
      closed: false,
    };
  }

  const start = path[0];
  const last = path[path.length - 1];

  if (x === start.x && y === start.y) {
    if (path.length < MIN_OPEN_CELLS_BEFORE_CLOSE) {
      return {
        error: `Au moins ${MIN_OPEN_CELLS_BEFORE_CLOSE} cases avant de fermer le circuit`,
      };
    }
    return {
      circuit: withPath(circuit, [...path, { x, y, type: TrackCellType.STRAIGHT }]),
      closed: true,
    };
  }

  if (isCellOnOpenPath(path, x, y)) {
    return { error: 'Case déjà sur le tracé' };
  }

  if (!areCellsAdjacent(last.x, last.y, x, y)) {
    return { error: 'La case doit être adjacente à la dernière' };
  }

  return {
    circuit: withPath(circuit, [...path, { x, y, type: TrackCellType.STRAIGHT }]),
    closed: false,
  };
}

/** Retire la dernière case du tracé (rouvre le circuit s’il était fermé). */
export function undoLastPathCell(circuit: Circuit): Circuit | null {
  let path = circuit.path;
  if (path.length === 0) return null;

  if (isPathClosed(path)) {
    path = path.slice(0, -1);
  }

  if (path.length === 0) return null;

  return withPath(circuit, path.slice(0, -1));
}

/** Zone visible de l’éditeur géométrique (grille entière ou tracé + marge). */
export function getGeometryEditorBounds(circuit: Circuit): CircuitPathBounds {
  if (circuit.path.length === 0) {
    return {
      minX: 0,
      minY: 0,
      maxX: circuit.width - 1,
      maxY: circuit.height - 1,
      widthCells: circuit.width,
      heightCells: circuit.height,
    };
  }

  let minX = circuit.path[0].x;
  let maxX = circuit.path[0].x;
  let minY = circuit.path[0].y;
  let maxY = circuit.path[0].y;
  const end = isPathClosed(circuit.path) ? circuit.path.length - 1 : circuit.path.length;
  for (let i = 0; i < end; i++) {
    minX = Math.min(minX, circuit.path[i].x);
    maxX = Math.max(maxX, circuit.path[i].x);
    minY = Math.min(minY, circuit.path[i].y);
    maxY = Math.max(maxY, circuit.path[i].y);
  }

  minX = Math.max(0, minX - 1);
  minY = Math.max(0, minY - 1);
  maxX = Math.min(circuit.width - 1, maxX + 1);
  maxY = Math.min(circuit.height - 1, maxY + 1);

  return {
    minX,
    minY,
    maxX,
    maxY,
    widthCells: maxX - minX + 1,
    heightCells: maxY - minY + 1,
  };
}

/** Cases où un clic en mode Tracer est accepté (aperçu visuel). */
export function getPlaceableDrawCells(circuit: Circuit): { x: number; y: number }[] {
  const path = circuit.path;
  if (isPathClosed(path) || path.length === 0) return [];

  const last = path[path.length - 1];
  const start = path[0];
  const candidates: { x: number; y: number }[] = [];

  if (path.length >= MIN_OPEN_CELLS_BEFORE_CLOSE) {
    candidates.push({ x: start.x, y: start.y });
  }

  const neighbors = [
    { x: last.x + 1, y: last.y },
    { x: last.x - 1, y: last.y },
    { x: last.x, y: last.y + 1 },
    { x: last.x, y: last.y - 1 },
  ];

  for (const cell of neighbors) {
    if (!isWithinGrid(circuit, cell.x, cell.y)) continue;
    if (isCellOnOpenPath(path, cell.x, cell.y)) continue;
    candidates.push(cell);
  }

  return candidates;
}
